import React from "react";
import { Download, Users, RefreshCw, Trash2, Shield, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import html2pdf from 'html2pdf.js';

export default function TestAccountsGuide() {
  
  const handleDownloadPDF = () => {
    // Hide the button before generating PDF
    const button = document.getElementById('download-test-guide-btn');
    if (button) button.style.display = 'none';
    
    const element = document.getElementById('test-accounts-guide-content');
    const opt = {
      margin: 0.5,
      filename: 'Golf_Eclectic_Test_Accounts_Guide.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    
    html2pdf().set(opt).from(element).save().then(() => {
      // Show button again after PDF is generated
      if (button) button.style.display = 'flex';
    });
  };

  return (
    <div id="test-accounts-guide-content" className="max-w-4xl mx-auto space-y-8">
      {/* Download Button */}
      <div id="download-test-guide-btn" className="flex justify-end mb-4 no-print">
        <Button onClick={handleDownloadPDF} className="flex items-center space-x-2 bg-ink hover:bg-ink-hover">
          <Download className="w-4 h-4" />
          <span>Download as PDF</span>
        </Button>
      </div>

      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <img 
            src="/logo.png" 
            alt="Golf Eclectic" 
            className="w-24 h-24 rounded-full"
          />
        </div>
        <h1 className="text-3xl font-semibold text-ink tracking-tight mb-2">Test Accounts Guide</h1>
        <p className="text-lg text-ink-muted">Golf Eclectic Application Testing System</p>
      </div>

      {/* Overview */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center">
          <Users className="w-6 h-6 mr-2" />
          Overview
        </h2>
        <p className="text-ink-muted leading-relaxed">
          The Golf Eclectic application includes a robust Test Account Management System that allows administrators 
          to create, manage, and reset test accounts for thorough pre-production and ongoing testing. This system 
          ensures safe testing without affecting real user data.
        </p>
      </section>

      {/* Test Account Structure */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">Test Account Email Structure</h2>
        <div className="bg-accent-tint-2 border-l-4 border-accent p-4 mb-4">
          <p className="font-semibold text-ink mb-2">All test accounts use the following email domain:</p>
          <ul className="list-disc list-inside text-ink-muted space-y-1">
            <li><strong>Domain:</strong> @golfeclectic.net</li>
            <li><strong>Format:</strong> [alias]@golfeclectic.net</li>
          </ul>
        </div>

        <h3 className="text-xl font-bold text-ink mb-3">Pre-Configured Test Accounts</h3>
        
        {/* Admin Account */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <h4 className="font-bold text-red-900 mb-2 flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            Test Admin Account
          </h4>
          <ul className="text-sm text-ink-muted space-y-1">
            <li><strong>Email:</strong> testadmin@golfeclectic.net</li>
            <li><strong>Password:</strong> TestAdmin2025!</li>
            <li><strong>Role:</strong> Administrator</li>
            <li><strong>Access:</strong> Full admin dashboard with all management capabilities</li>
          </ul>
        </div>

        {/* Player Accounts */}
        <div className="space-y-3">
          <h4 className="font-bold text-ink">Test Player Accounts</h4>
          
          {[1, 2, 3, 4].map((num) => (
            <div key={num} className="bg-app border border-line rounded-lg p-4">
              <h5 className="font-bold text-accent-deep mb-2">Player {num}</h5>
              <ul className="text-sm text-ink-muted space-y-1">
                <li><strong>Email:</strong> tester{num}@golfeclectic.net</li>
                <li><strong>Password:</strong> TestPlayer2025!</li>
                <li><strong>Role:</strong> Player</li>
                <li><strong>Use Case:</strong> {
                  num === 1 ? "Primary player testing account" :
                  num === 2 ? "Secondary player for multi-player scenarios" :
                  num === 3 ? "Leaderboard ranking and achievement testing" :
                  "Certificate generation (need 3+ players) and additional testing"
                }</li>
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Password Persistence */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">Password Persistence Across Deployments</h2>
        <div className="bg-accent-tint-2 border-l-4 border-accent p-4 mb-4">
          <h3 className="font-bold text-accent-deep mb-2 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            How It Works
          </h3>
          <p className="text-ink-muted leading-relaxed mb-3">
            Test account passwords persist across deployments and data resets because:
          </p>
          <ol className="list-decimal list-inside text-ink-muted space-y-1">
            <li><strong>Account Records Preserved:</strong> Test account user records remain in the database during "reset" operations</li>
            <li><strong>Password Hashes Retained:</strong> Bcrypt password hashes are stored with the account and not deleted during resets</li>
            <li><strong>Only Data Cleared:</strong> Reset operations only clear activity data (rounds, scores, messages), not account credentials</li>
          </ol>
        </div>

        <h3 className="text-xl font-bold text-ink mb-3">When Passwords Are Lost</h3>
        <p className="text-ink-muted mb-2">Passwords are only lost when:</p>
        <ul className="list-disc list-inside text-ink-muted space-y-1 mb-4">
          <li>Test account is <strong>deleted</strong> (not reset)</li>
          <li>Database is completely wiped</li>
          <li>Manual database cleanup removes user records</li>
        </ul>

        <h3 className="text-xl font-bold text-ink mb-3">Best Practices</h3>
        <ol className="list-decimal list-inside text-ink-muted space-y-1">
          <li><strong>Document Passwords:</strong> Keep test account passwords documented in secure location</li>
          <li><strong>Use Consistent Passwords:</strong> Use the same password pattern for easy testing</li>
          <li><strong>Reset vs Delete:</strong> Use "Reset" to clear data while keeping accounts; only "Delete" when removing accounts permanently</li>
        </ol>
      </section>

      {/* Test Account Management */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center">
          <RefreshCw className="w-6 h-6 mr-2" />
          Test Account Management System
        </h2>
        
        <h3 className="text-xl font-bold text-ink mb-3">Accessing Test Account Management</h3>
        <ol className="list-decimal list-inside text-ink-muted space-y-1 mb-4">
          <li>Log in as an administrator</li>
          <li>Navigate to <strong>Dashboard → Admin Utilities</strong></li>
          <li>Scroll to the <strong>Test Account Management</strong> section</li>
        </ol>

        <h3 className="text-xl font-bold text-ink mb-3">Creating New Test Accounts</h3>
        <div className="bg-app border border-line rounded-lg p-4 mb-4">
          <p className="font-semibold text-ink mb-2">Via Admin Utilities UI:</p>
          <ol className="list-decimal list-inside text-ink-muted space-y-1">
            <li>Click <strong>"Create Test Account"</strong> button</li>
            <li>Fill in the form:
              <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                <li><strong>Name:</strong> Full name of test user</li>
                <li><strong>Email:</strong> Must use @golfeclectic.net domain</li>
                <li><strong>Password:</strong> Secure password for the test account</li>
                <li><strong>Is Admin:</strong> Toggle ON for admin accounts, OFF for player accounts</li>
              </ul>
            </li>
            <li>Click <strong>"Create Account"</strong></li>
            <li>Account is immediately available for login</li>
          </ol>
        </div>

        <h3 className="text-xl font-bold text-ink mb-3">Resetting Test Accounts</h3>
        
        <div className="bg-warm-tint/40 border border-warm/40 rounded-lg p-4 mb-3">
          <h4 className="font-bold text-warm mb-2">Reset Individual Account:</h4>
          <p className="text-sm text-ink-muted mb-2"><strong>Purpose:</strong> Clears all data associated with a single test account while keeping the account credentials intact</p>
          <p className="text-sm text-ink-muted mb-2"><strong>What Gets Deleted:</strong></p>
          <ul className="list-disc list-inside text-sm text-ink-muted space-y-1 ml-4 mb-2">
            <li>Competition registrations</li>
            <li>Submitted rounds and scores</li>
            <li>Best scores</li>
            <li>Messages read status</li>
            <li>Message preferences</li>
            <li>Social feed posts, replies, and reactions</li>
          </ul>
          <p className="text-sm text-ink-muted mb-2"><strong>What Gets Preserved:</strong></p>
          <ul className="list-disc list-inside text-sm text-ink-muted space-y-1 ml-4">
            <li>Account credentials (email, password)</li>
            <li>Account name</li>
            <li>Role (admin/player)</li>
            <li>Test account flag</li>
          </ul>
        </div>

        <div className="bg-warm-tint/40 border border-warm/40 rounded-lg p-4 mb-3">
          <h4 className="font-bold text-warm mb-2">Reset All Test Accounts:</h4>
          <p className="text-sm text-ink-muted mb-2"><strong>Purpose:</strong> Quickly reset all test accounts at once for comprehensive testing cycles</p>
          <p className="text-sm text-ink-muted mb-2"><strong>Use Case:</strong> Perfect for resetting testing environment before each deployment or major test cycle</p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-bold text-red-900 mb-2 flex items-center">
            <Trash2 className="w-5 h-5 mr-2" />
            Deleting Test Accounts:
          </h4>
          <p className="text-sm text-ink-muted mb-2"><strong>Purpose:</strong> Permanently remove a test account from the system</p>
          <p className="text-sm text-red-700 font-semibold">⚠️ Warning: This action cannot be undone</p>
        </div>
      </section>

      {/* Testing Workflows */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">Testing Workflows</h2>
        
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-bold text-ink mb-3">Admin Testing Workflow</h3>
            <p className="text-ink-muted mb-2"><strong>Login:</strong> Use testadmin@golfeclectic.net</p>
            <div className="bg-app border border-line rounded-lg p-4">
              <p className="font-semibold text-ink mb-2">Test Scenarios:</p>
              <ul className="list-disc list-inside text-sm text-ink-muted space-y-1">
                <li>Competition Management (create, edit, delete)</li>
                <li>Player Management (add, remove)</li>
                <li>Score Management (enter, edit, delete rounds)</li>
                <li>Hole-in-One and Score of 2 detection</li>
                <li>Messaging & Communication</li>
                <li>Reports & Exports (Excel, PDFs)</li>
                <li>Email Top 3 certificates</li>
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold text-ink mb-3">Player Testing Workflow</h3>
            <p className="text-ink-muted mb-2"><strong>Login:</strong> Use tester1@golfeclectic.net, tester2@golfeclectic.net, etc.</p>
            <div className="bg-app border border-line rounded-lg p-4">
              <p className="font-semibold text-ink mb-2">Test Scenarios:</p>
              <ul className="list-disc list-inside text-sm text-ink-muted space-y-1">
                <li>Join/leave competitions</li>
                <li>Score entry (initial and subsequent rounds)</li>
                <li>Hole-in-One and Score of 2 workflows</li>
                <li>View best scores and achievements</li>
                <li>Social feed interaction</li>
                <li>Message management and preferences</li>
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold text-ink mb-3">Multi-Player Testing</h3>
            <p className="text-ink-muted mb-2"><strong>Use:</strong> Multiple test player accounts simultaneously</p>
            <div className="bg-app border border-line rounded-lg p-4">
              <p className="font-semibold text-ink mb-2">Scenarios:</p>
              <ul className="list-disc list-inside text-sm text-ink-muted space-y-1">
                <li>Multiple players join same competition</li>
                <li>Leaderboard ranking verification (by net score)</li>
                <li>Social feed interactions between players</li>
                <li>Achievement visibility across players</li>
                <li>Top 3 certificate generation</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testing Checklist */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">Pre-Deployment Testing Checklist</h2>
        <div className="bg-app border border-line rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-bold text-accent-deep mb-2">Admin Testing</h4>
              <ul className="text-sm text-ink-muted space-y-1">
                <li>☐ Admin login</li>
                <li>☐ Create competition</li>
                <li>☐ Add players to competition</li>
                <li>☐ Enter scores for players</li>
                <li>☐ Test Hole-in-One workflow</li>
                <li>☐ Test Score of 2 workflow</li>
                <li>☐ View player best scores</li>
                <li>☐ Generate reports (Excel, PDFs)</li>
                <li>☐ Email Top 3 certificates</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-accent-deep mb-2">Player Testing</h4>
              <ul className="text-sm text-ink-muted space-y-1">
                <li>☐ Player login</li>
                <li>☐ Join competition</li>
                <li>☐ Enter rounds</li>
                <li>☐ Test achievements</li>
                <li>☐ View best scores</li>
                <li>☐ Social feed posting</li>
                <li>☐ Message management</li>
                <li>☐ Leave competition</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">Troubleshooting</h2>
        <div className="space-y-3">
          <div className="bg-app border-l-4 border-accent p-3">
            <p className="font-semibold text-ink">Problem: Cannot log in with test account</p>
            <p className="text-sm text-ink-muted mt-1">Solutions:</p>
            <ul className="list-disc list-inside text-sm text-ink-muted ml-4">
              <li>Verify account exists in Admin Utilities</li>
              <li>Check email spelling (must be exact)</li>
              <li>Verify password matches documented password</li>
              <li>Try resetting account and recreating if needed</li>
            </ul>
          </div>

          <div className="bg-app border-l-4 border-accent p-3">
            <p className="font-semibold text-ink">Problem: Test data not clearing</p>
            <p className="text-sm text-ink-muted mt-1">Solutions:</p>
            <ul className="list-disc list-inside text-sm text-ink-muted ml-4">
              <li>Use "Reset All" instead of individual resets</li>
              <li>Check browser cache and clear if needed</li>
              <li>Verify account shows as test account (🧪)</li>
              <li>Use Database Admin Utilities for manual cleanup</li>
            </ul>
          </div>

          <div className="bg-app border-l-4 border-accent p-3">
            <p className="font-semibold text-ink">Problem: Achievement badges not showing</p>
            <p className="text-sm text-ink-muted mt-1">Solutions:</p>
            <ul className="list-disc list-inside text-sm text-ink-muted ml-4">
              <li>Verify player has achieved scores of 1 or 2</li>
              <li>Check competition has "Recognize Score of 2" enabled (for diamonds)</li>
              <li>Ensure achievements are on unique holes</li>
              <li>Refresh page to update badge counts</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Footer */}
      <section className="mt-8 pt-6 border-t border-line text-center">
        <p className="text-sm text-ink-muted">
          <strong>Golf Eclectic Application</strong> - Test Accounts Guide
        </p>
        <p className="text-xs text-ink-soft mt-1">
          Last Updated: January 2025 | Version 1.0 Production Ready
        </p>
      </section>
    </div>
  );
}
